import React from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Film, Clock, Sparkles } from "lucide-react";

/**
 * Preview the AI-generated video storyboard (slide headings, bullets, narration).
 * The storyboard is saved to `lesson.video_storyboard` for a future text-to-video pipeline.
 */
export default function VideoStoryboardDialog({ open, onOpenChange, storyboard, onUse, onDiscard }) {
    if (!storyboard) return null;
    const slides = storyboard.slides || [];
    const mins = Math.round((storyboard.estimated_duration_sec || slides.length * 35) / 60);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="video-storyboard-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Film className="h-5 w-5 text-primary" />
                        Video storyboard — {storyboard.title || "Lesson"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        AI-generated slide-by-slide storyboard with narration for this lesson.
                    </DialogDescription>
                    <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                        <Badge variant="secondary" className="gap-1">
                            <Sparkles className="h-3 w-3" /> AI generated
                        </Badge>
                        <Badge variant="outline" className="gap-1">
                            <Clock className="h-3 w-3" /> ~{mins} min · {slides.length} slides
                        </Badge>
                    </div>
                </DialogHeader>

                <ScrollArea className="max-h-[55vh] pr-2">
                    <ol className="space-y-4" data-testid="storyboard-slide-list">
                        {slides.map((s, idx) => (
                            <li
                                key={idx}
                                className="rounded-lg border border-border bg-muted/30 p-4"
                                data-testid={`storyboard-slide-${idx}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-display text-base text-primary">
                                        Slide {s.number || idx + 1} · {s.heading || "Untitled"}
                                    </h4>
                                </div>
                                {(s.bullets || []).length > 0 && (
                                    <ul className="list-disc pl-5 text-sm text-foreground/90 mb-2 space-y-0.5">
                                        {(s.bullets || []).map((b, i) => (
                                            <li key={i}>{b}</li>
                                        ))}
                                    </ul>
                                )}
                                {s.narration && (
                                    <p className="text-sm italic text-foreground/75 leading-relaxed border-l-2 border-accent pl-3 mt-2">
                                        {s.narration}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                </ScrollArea>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onDiscard}
                        data-testid="storyboard-discard-btn"
                    >
                        Discard
                    </Button>
                    <Button
                        onClick={onUse}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="storyboard-use-btn"
                    >
                        Save storyboard to lesson
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
